import { createContext, useContext, useState, useEffect } from "react";

// Layout definitions - completely different page structures
const layouts = {
  layout_1: {
    id: "layout_1",
    name: "Stories/Fullscreen",
    description: "Vollbild-Swipe wie Instagram Stories",
    icon: "📱",
    structure: "fullscreen-vertical-swipe"
  },
  layout_2: {
    id: "layout_2",
    name: "Card Stack",
    description: "Tinder-Style Karten zum Swipen",
    icon: "🃏",
    structure: "card-stack-swipe"
  },
  layout_3: {
    id: "layout_3",
    name: "Magazine Grid",
    description: "Editorial Masonry-Layout wie Pinterest",
    icon: "📰",
    structure: "masonry-grid"
  },
  layout_4: {
    id: "layout_4",
    name: "Tab Categories",
    description: "Kategorie-Tabs mit horizontalem Scroll",
    icon: "📑",
    structure: "tabbed-horizontal"
  },
  layout_5: {
    id: "layout_5",
    name: "Classic List",
    description: "Klassische vertikale Liste",
    icon: "📋",
    structure: "vertical-list"
  }
};

// Color themes
const themes = {
  light: {
    id: "light",
    name: "Hell",
    colors: {
      bgMain: "#FAFAFA",
      bgCard: "#FFFFFF",
      textPrimary: "#1A1A1A",
      textSecondary: "#6B7280",
      primary: "#2D5016",
      accent: "#E8F5E9",
      border: "#E5E7EB"
    }
  },
  retro: {
    id: "retro",
    name: "Retro",
    colors: {
      bgMain: "#FDFBF7",
      bgCard: "#F5F5F0",
      textPrimary: "#3E2723",
      textSecondary: "#5D4037",
      primary: "#2D5016",
      accent: "#D7CCC8",
      border: "#8D6E63"
    }
  },
  nature: {
    id: "nature",
    name: "Natur",
    colors: {
      bgMain: "#F1F8E9",
      bgCard: "#FFFFFF",
      textPrimary: "#1B5E20",
      textSecondary: "#33691E",
      primary: "#2D5016",
      accent: "#C8E6C9",
      border: "#A5D6A7"
    }
  },
  premium: {
    id: "premium",
    name: "Premium",
    colors: {
      bgMain: "#121212",
      bgCard: "#1E1E1E",
      textPrimary: "#FFFFFF",
      textSecondary: "#A1A1AA",
      primary: "#C0A062",
      accent: "#2D5016",
      border: "#333333"
    }
  },
  modern: {
    id: "modern",
    name: "Modern",
    colors: {
      bgMain: "#FFFFFF",
      bgCard: "#F3F4F6",
      textPrimary: "#111827",
      textSecondary: "#4B5563",
      primary: "#16A34A",
      accent: "#DCFCE7",
      border: "#E5E7EB"
    }
  }
};

const LayoutContext = createContext(null);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within LayoutProvider");
  }
  return context;
};

export const LayoutProvider = ({ children }) => {
  const [currentLayout, setCurrentLayout] = useState("layout_5"); // Default: Classic List
  const [currentTheme, setCurrentTheme] = useState("light");
  
  const layout = layouts[currentLayout];
  const theme = themes[currentTheme];

  useEffect(() => {
    // Apply theme colors to document
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
    document.body.style.backgroundColor = theme.colors.bgMain;
    document.body.style.color = theme.colors.textPrimary;
  }, [theme]);

  const switchLayout = (layoutId) => {
    if (layouts[layoutId]) {
      setCurrentLayout(layoutId);
    }
  };

  const switchTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  const getAllLayouts = () => Object.values(layouts);
  const getAllThemes = () => Object.values(themes);

  return (
    <LayoutContext.Provider value={{ 
      layout, 
      theme, 
      currentLayout, 
      currentTheme,
      switchLayout, 
      switchTheme,
      getAllLayouts,
      getAllThemes,
      layouts,
      themes
    }}>
      {children}
    </LayoutContext.Provider>
  );
};

export default LayoutProvider;
